/**
 * Procedural lens dirt (style guide §3.5: High only, gain 0.08). Generated once at load from a seed, so the
 * `tex.lens` texture set is not needed (its 100,000 B ledger line is released). Pure; tested.
 * Layers, in the order a real front element collects them: grease films, irregular smudges (a few of them
 * bokeh-like rings from dried droplets), wipe smears along curved strokes and dust specks, all modulated by a
 * two-octave value noise so no two areas of the glass look alike.
 */
import { createRng, nextFloat, range, type Rng } from '../../core/rng'
import type { PostFile } from './data'

export type DirtConfig = PostFile['dirt']

/** One splat's shape; reused for every splat (the generator runs once at load, but stays lean). */
interface Shape {
  /** Radius along the major axis (px) and minor/major ratio (1 = round). */
  r: number
  aspect: number
  rot: number
  /** Radius wobble amplitude (fraction), lobe count and phase: irregular outlines. */
  wobble: number
  lobes: number
  phase: number
  /** Edge softness as a fraction of the radius (1 = soft all the way to the centre). */
  soft: number
  /** Ring strength (0 = flat blob, 1 = bright rim, dark centre). */
  rim: number
  gaussian: boolean
}

function newShape(): Shape {
  return { r: 1, aspect: 1, rot: 0, wobble: 0, lobes: 0, phase: 0, soft: 0.2, rim: 0, gaussian: true }
}

function smooth(t: number): number {
  const c = t < 0 ? 0 : t > 1 ? 1 : t
  return c * c * (3 - 2 * c)
}

/** Adds `a * profile` over the shape's footprint. */
function splat(buf: Float32Array, w: number, h: number, cx: number, cy: number, a: number, s: Shape): void {
  const reach = s.r * (1 + s.wobble)
  const x0 = Math.max(0, Math.floor(cx - reach))
  const x1 = Math.min(w - 1, Math.ceil(cx + reach))
  const y0 = Math.max(0, Math.floor(cy - reach))
  const y1 = Math.min(h - 1, Math.ceil(cy + reach))
  const inv = 1 / Math.max(s.r, 1e-6)
  const cos = Math.cos(s.rot)
  const sin = Math.sin(s.rot)
  const invAspect = 1 / Math.max(s.aspect, 1e-3)
  for (let y = y0; y <= y1; y++) {
    const py = (y + 0.5 - cy) * inv
    for (let x = x0; x <= x1; x++) {
      const px = (x + 0.5 - cx) * inv
      const u = px * cos + py * sin
      const v = (-px * sin + py * cos) * invAspect
      let d = Math.sqrt(u * u + v * v)
      if (s.wobble > 0) d /= 1 + s.wobble * Math.sin(s.lobes * Math.atan2(v, u) + s.phase)
      if (d >= 1) continue
      let val: number
      if (s.gaussian) val = Math.exp(-4 * d * d) * (1 - d)
      else {
        const edge = 1 - smooth((d - (1 - s.soft)) / Math.max(s.soft, 1e-3))
        val = edge * (1 - s.rim + s.rim * smooth((d - 0.45) / 0.55))
      }
      buf[y * w + x] += a * val
    }
  }
}

/** Two-octave value noise in [floor, 1] over the texture. */
function noiseField(rng: Rng, n: DirtConfig['noise'], w: number, h: number, out: Float32Array): void {
  out.fill(0)
  let weight = 0
  for (let octave = 0; octave < 2; octave++) {
    const cells = n.cells * (octave + 1) * 2 - n.cells
    const amp = octave === 0 ? 0.7 : 0.3
    weight += amp
    const cx = cells + 1
    const cy = Math.max(2, Math.round((cells * h) / w)) + 1
    const lattice = new Float32Array(cx * cy)
    for (let i = 0; i < lattice.length; i++) lattice[i] = nextFloat(rng)
    for (let y = 0; y < h; y++) {
      const fy = (y / h) * (cy - 1)
      const iy = Math.min(cy - 2, Math.floor(fy))
      const ty = smooth(fy - iy)
      for (let x = 0; x < w; x++) {
        const fx = (x / w) * (cx - 1)
        const ix = Math.min(cx - 2, Math.floor(fx))
        const tx = smooth(fx - ix)
        const i0 = iy * cx + ix
        const a = lattice[i0] + (lattice[i0 + 1] - lattice[i0]) * tx
        const b = lattice[i0 + cx] + (lattice[i0 + cx + 1] - lattice[i0 + cx]) * tx
        out[y * w + x] += amp * (a + (b - a) * ty)
      }
    }
  }
  for (let i = 0; i < out.length; i++) out[i] = n.floor + (1 - n.floor) * (out[i] / weight)
}

/** Generates the dirt mask: one byte per pixel (0..255 = 0..1). Deterministic for a seed. */
export function generateLensDirt(cfg: DirtConfig): Uint8Array {
  const w = cfg.width
  const h = cfg.height
  const rng = createRng(cfg.seed)
  const buf = new Float32Array(w * h)
  const s = newShape()
  // Sizes are fractions of the width (specks: pixels) so the look does not change with the texture size.
  const g = cfg.grease
  for (let i = 0; i < g.count; i++) {
    s.r = range(rng, g.rMin, g.rMax) * w
    s.aspect = range(rng, 0.5, 1)
    s.rot = range(rng, 0, Math.PI)
    s.wobble = 0
    s.gaussian = true
    splat(buf, w, h, range(rng, 0, w), range(rng, 0, h), range(rng, g.aMin, g.aMax), s)
  }
  const sm = cfg.smudges
  for (let i = 0; i < sm.count; i++) {
    // Many small, few large: square the random radius.
    const u = nextFloat(rng)
    s.r = (sm.rMin + (sm.rMax - sm.rMin) * u * u) * w
    s.aspect = range(rng, 1 / sm.aspectMax, 1)
    s.rot = range(rng, 0, Math.PI)
    s.wobble = range(rng, 0, sm.wobble)
    s.lobes = Math.floor(range(rng, 2, 4))
    s.phase = range(rng, 0, Math.PI * 2)
    s.soft = range(rng, sm.softMin, sm.softMax)
    const ring = nextFloat(rng) < sm.rimChance
    s.rim = ring ? sm.rim : 0
    s.gaussian = false
    if (ring) {
      // Dried droplets are round and crisp.
      s.aspect = 1
      s.wobble *= 0.25
      s.soft = sm.softMin
    }
    splat(buf, w, h, range(rng, 0, w), range(rng, 0, h), range(rng, sm.aMin, sm.aMax), s)
  }
  const st = cfg.streaks
  for (let i = 0; i < st.count; i++) {
    let x = range(rng, 0, w)
    let y = range(rng, 0, h)
    let ang = range(rng, 0, Math.PI * 2)
    const turn = range(rng, -st.arc, st.arc) / st.steps
    const step = (range(rng, st.lenMin, st.lenMax) * w) / st.steps
    const r = range(rng, st.rMin, st.rMax) * w
    const a = range(rng, st.aMin, st.aMax)
    s.aspect = st.aspect
    s.wobble = 0
    s.gaussian = true
    for (let k = 0; k < st.steps; k++) {
      s.r = r * (0.6 + 0.4 * Math.sin((Math.PI * (k + 0.5)) / st.steps))
      s.rot = ang
      splat(buf, w, h, x, y, a * Math.sin((Math.PI * (k + 0.5)) / st.steps), s)
      x += Math.cos(ang) * step
      y += Math.sin(ang) * step
      ang += turn
    }
  }
  const sp = cfg.specks
  s.aspect = 1
  s.wobble = 0
  s.gaussian = true
  for (let i = 0; i < sp.count; i++) {
    const u = nextFloat(rng)
    s.r = sp.rMin + (sp.rMax - sp.rMin) * u * u
    splat(buf, w, h, range(rng, 0, w), range(rng, 0, h), range(rng, sp.aMin, sp.aMax), s)
  }
  const noise = new Float32Array(w * h)
  noiseField(rng, cfg.noise, w, h, noise)
  const out = new Uint8Array(w * h)
  for (let i = 0; i < out.length; i++) {
    const v = buf[i] * noise[i]
    out[i] = Math.round(255 * (v < 1 ? v : 1))
  }
  return out
}
